import { useEffect } from 'react';
import {
  ChakraBox,
  ChakraFlex,
  CheckboxV2,
  DateTimeInput,
  FAIconName,
  FireflyButton,
  FireflyTertiaryButtonMenu,
  InputGroupV2,
  ModalV2,
  SkeletonLoader,
  Text,
  TextInputV2,
  ValidationMessage,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';
import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import { GroupClassHostsField } from '~/components/maud/calendar/group-class/GroupClassHostsField';
import { GroupClassCommonField } from '~/components/maud/calendar/group-class/GroupClassCommonField';
import { GroupClassDateField } from '~/components/maud/calendar/group-class/GroupClassDateField';
import {
  DayOfWeekOptions,
  DurationOptions,
} from '~/lib/constants/groupClasses.constants';
import { ValidationType } from '~/lib/constants/enumeration.constants';
import { TextInputV2Type } from '~/lib/constants/inquiry.constants';
import { getTimezoneAbbreviation } from '~/lib/util/timezone.util';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useCreateEditSection } from './useCreateEditSection';

const TOP_LEVEL_ID = 'group-section-modal';

interface CreateEditSectionModalProps {
  groupStatusCode: GroupStatusType;
  groupId: string;
  groupSectionId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateEditSectionModal = ({
  isOpen,
  onClose,
  onSuccess,
  groupId,
  groupSectionId,
  groupStatusCode,
}: CreateEditSectionModalProps) => {
  const { trackEvent } = useAnalytics();

  const {
    formState,
    mergedStartDateTime,
    isWarningCloseModalOpen,
    setIsWarningCloseModalOpen,
    hasUserEditedStartDate,
    setHasUserEditedStartDate,
    hasUserEditedMaxAttendees,
    setHasUserEditedMaxAttendees,
    isEditMode,
    isFieldDisabledForLiveGroup,
    shouldDisableDayOfWeek,
    effectiveEndDate,
    shouldDisableSubmit,
    frequencyOptions,
    sessionOptions,
    timeZones,
    isFetchingSection,
    isCreating,
    isEditing,
    startDateTimeError,
    dayOfWeekMismatchError,
    maximumAttendeesError,
    updateFormField,
    handleStartTimeChange,
    handleClose,
    handleConfirmClose,
    handleSubmit,
    getEndDateMinDate,
  } = useCreateEditSection({
    groupStatusCode,
    groupId,
    groupSectionId,
    isOpen,
    onClose,
    onSuccess,
  });

  useEffect(() => {
    if (isOpen && !isFetchingSection) {
      const event = isEditMode
        ? GroupSectionEvents.editSectionModalViewed
        : GroupSectionEvents.addSectionModalViewed;
      trackEvent(event, {
        Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
        groupId,
        ...(groupSectionId && { groupSectionId }),
      });
    }
  }, [
    isOpen,
    isFetchingSection,
    isEditMode,
    groupId,
    groupSectionId,
    trackEvent,
  ]);

  return (
    <ModalV2
      id={`${TOP_LEVEL_ID}__modal`}
      footerContent={
        <ChakraFlex justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            onClick={handleClose}
            variant="secondary"
            leftIcon={FAIconName.XmarkRegular}
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__submit`}
            onClick={handleSubmit}
            variant="primary"
            leftIcon={
              isEditMode ? FAIconName.checkRegular : FAIconName.plusRegular
            }
            isDisabled={shouldDisableSubmit}
            isLoading={isCreating || isEditing}
          >
            {isEditMode ? 'Save changes' : 'Add section'}
          </FireflyButton>
        </ChakraFlex>
      }
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title={`${isEditMode ? 'Edit' : 'Add'} section`}
    >
      <SkeletonLoader
        id={`${TOP_LEVEL_ID}__skeleton-loader`}
        isLoaded={!isFetchingSection}
      >
        <ChakraBox overflowX="hidden">
          <ChakraFlex flexDirection="column" gap="18px">
            <GroupClassHostsField
              onChange={(value) => updateFormField('hosts', value)}
              value={formState.hosts}
            />
            <GroupClassCommonField
              label="Frequency"
              leftIcon={FAIconName.redoRegular}
              onChange={(value) => updateFormField('frequency', value)}
              selectedValue={formState.frequency}
              shouldShowAsteriskWhenRequired
              options={frequencyOptions}
            />
            <GroupClassCommonField
              isDisabled={shouldDisableDayOfWeek || isFieldDisabledForLiveGroup}
              key={`day-of-week-${shouldDisableDayOfWeek ? 'disabled' : 'enabled'}`}
              label="Day of week"
              leftIcon={FAIconName.calendarDayRegular}
              onChange={(value) => updateFormField('dayOfWeek', value)}
              selectedValue={
                shouldDisableDayOfWeek ? undefined : formState.dayOfWeek
              }
              shouldShowAsteriskWhenRequired={!shouldDisableDayOfWeek}
              shouldHideOptionalLabel={shouldDisableDayOfWeek}
              options={DayOfWeekOptions}
            />
            <ChakraFlex gap="18px">
              <ChakraBox position="relative">
                <InputGroupV2 id={`${TOP_LEVEL_ID}__start-time`}>
                  <DateTimeInput
                    hideDatePicker
                    id={`${TOP_LEVEL_ID}__date-time-input`}
                    labelText="Start time"
                    isRequired
                    shouldShowAsteriskWhenRequired
                    onChange={handleStartTimeChange}
                    selectedDate={
                      mergedStartDateTime.startTime
                        ? DateTime.fromFormat(
                            mergedStartDateTime.startTime,
                            'HH:mm:ss',
                          ).toJSDate()
                        : null
                    }
                    isDisabled={isFieldDisabledForLiveGroup}
                  />
                  {startDateTimeError && (
                    <ChakraBox maxWidth="186px" overflow="hidden">
                      <ValidationMessage
                        id={`${TOP_LEVEL_ID}__start-datetime-error-message`}
                        type={ValidationType.CRITICAL}
                        value={startDateTimeError}
                      />
                    </ChakraBox>
                  )}
                </InputGroupV2>

                <ChakraBox position="absolute" top={0} right="8px">
                  <FireflyTertiaryButtonMenu
                    buttonId={`${TOP_LEVEL_ID}__timezone-select`}
                    buttonText={getTimezoneAbbreviation(formState.timezone)}
                    maxHeight="300px"
                    onOptionClick={(option) =>
                      updateFormField('timezone', option)
                    }
                    options={timeZones}
                    isDisabled={isFieldDisabledForLiveGroup}
                  />
                </ChakraBox>
              </ChakraBox>

              <GroupClassCommonField
                label="Duration"
                leftIcon={FAIconName.timerRegular}
                onChange={(value) => updateFormField('duration', Number(value))}
                selectedValue={
                  formState.duration ? String(formState.duration) : undefined
                }
                shouldShowAsteriskWhenRequired
                options={DurationOptions}
              />
            </ChakraFlex>
          </ChakraFlex>

          <ChakraFlex flexDirection="column" gap="18px" marginTop="36px">
            <Text>Optional attributes</Text>
            <GroupClassCommonField
              key={`length-${formState.length}`}
              label="Length"
              leftIcon={FAIconName.arrowsLeftRight}
              onChange={(value) => updateFormField('length', value)}
              selectedValue={formState.length || ''}
              modalId={`${TOP_LEVEL_ID}__modal`}
              shouldShowAsteriskWhenRequired={false}
              shouldHideOptionalLabel
              options={sessionOptions}
              isDisabled={isFieldDisabledForLiveGroup}
            />
            <ChakraFlex gap="18px">
              <GroupClassDateField
                id={`${TOP_LEVEL_ID}__start-date`}
                key={`start-date-${formState.startDate}`}
                label="Start date"
                leftIcon={FAIconName.arrowFromLeft}
                onChange={(date) => {
                  setHasUserEditedStartDate(true);
                  updateFormField('startDate', date);
                }}
                value={
                  hasUserEditedStartDate || !isEditMode || formState.showDate
                    ? formState.startDate
                    : ''
                }
                minDate={new Date()}
                isDisabled={isFieldDisabledForLiveGroup}
                errorMessage={dayOfWeekMismatchError}
                placeholder="Please choose"
              />
              <GroupClassDateField
                id={`${TOP_LEVEL_ID}__end-date`}
                key={`end-date-${formState.endDate}`}
                label="End date"
                leftIcon={FAIconName.arrowToRightRegular}
                onChange={(date) => updateFormField('endDate', date)}
                value={effectiveEndDate}
                minDate={getEndDateMinDate(formState.startDate)}
                isRequired={false}
                isDisabled={formState.frequency === 'DOES_NOT_REPEAT'}
                placeholder="Please choose"
              />
            </ChakraFlex>

            <InputGroupV2
              id={`${TOP_LEVEL_ID}__max-attendees`}
              label="Max attendees"
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}__max-attendees-input`}
                isInvalid={!!maximumAttendeesError}
                placeholder="Please choose"
                onChange={(value) => {
                  const numericValue = Number(value);
                  if (!Number.isFinite(numericValue)) return;
                  updateFormField('maximumAttendees', Math.trunc(numericValue));
                  setHasUserEditedMaxAttendees(true);
                }}
                leftIcon={FAIconName.arrowUpToLine}
                type={TextInputV2Type.NUMBER}
                value={
                  (hasUserEditedMaxAttendees ||
                    !isEditMode ||
                    formState.showMaximumAttendees) &&
                  formState.maximumAttendees
                    ? String(formState.maximumAttendees)
                    : ''
                }
              />
              {maximumAttendeesError && (
                <ValidationMessage
                  id={`${TOP_LEVEL_ID}__max-attendees-error-message`}
                  type={ValidationType.CRITICAL}
                  value={maximumAttendeesError}
                />
              )}
            </InputGroupV2>

            <ChakraFlex flexDirection="column" gap="12px">
              <CheckboxV2
                id={`${TOP_LEVEL_ID}__all-classes-required`}
                isChecked={formState.mustAttendAll}
                label="All classes required"
                onChange={() =>
                  updateFormField('mustAttendAll', !formState.mustAttendAll)
                }
                isDisabled={isFieldDisabledForLiveGroup}
              />
              <CheckboxV2
                id={`${TOP_LEVEL_ID}__mark-as-webinar`}
                isChecked={formState.isWebinar}
                label="Mark as webinar"
                onChange={() =>
                  updateFormField('isWebinar', !formState.isWebinar)
                }
              />
            </ChakraFlex>
          </ChakraFlex>
          <DiscardChangesModalV2
            isOpen={isWarningCloseModalOpen}
            onClose={() => setIsWarningCloseModalOpen(false)}
            onConfirm={handleConfirmClose}
          />
        </ChakraBox>
      </SkeletonLoader>
    </ModalV2>
  );
};

export default CreateEditSectionModal;
